import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, MapPin, User, Phone, Mail, Building, Loader2, CheckCircle, Home } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import SMSConsentCheckbox from '../ui/SMSConsentCheckbox';
import { useAuth } from '../../contexts/AuthContext';
import AddressAutocomplete from '../ui/AddressAutocomplete';
import axios from 'axios';

const NewEstimate = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    street_address: '',
    address_line_2: '',
    city: '',
    state: '',
    zip_code: '',
    customerFirstName: '',
    customerLastName: '',
    customerEmail: '',
    customerPhone: '',
    smsConsent: false,
    scheduledInstallDate: '',
    current_roof_type: 'asphalt',
    desired_roof_type: 'asphalt',
    complexity: 'simple'
  });
  
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState('');

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  // Auto-populate from URL query params (when coming from a CRM lead card)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const updates = {};
    
    const getParam = (key) => {
      const v = params.get(key);
      return (v && v !== 'None' && v !== 'null' && v !== 'undefined') ? v : '';
    };
    
    if (getParam('first_name')) updates.customerFirstName = getParam('first_name');
    if (getParam('last_name')) updates.customerLastName = getParam('last_name');
    if (getParam('email')) updates.customerEmail = getParam('email');
    if (getParam('phone')) updates.customerPhone = getParam('phone');
    if (getParam('address')) updates.street_address = getParam('address');
    if (getParam('roof_type')) updates.desired_roof_type = getParam('roof_type');
    if (getParam('lead_id')) updates._leadId = getParam('lead_id');
    
    if (Object.keys(updates).length > 0) {
      setFormData(prev => ({ ...prev, ...updates }));
    }
  }, []);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!user?.id) {
      setError('User not authenticated');
      return;
    }

    setIsGenerating(true);
    setError('');

    try {
      // Construct full address from parts
      const fullAddress = `${formData.street_address}${formData.address_line_2 ? ', ' + formData.address_line_2 : ''}, ${formData.city}, ${formData.state} ${formData.zip_code}`;
      
      console.log('=== ESTIMATE CREATION DEBUG ===');
      console.log('Backend URL:', BACKEND_URL);
      console.log('Full Address:', fullAddress);
      console.log('User ID:', user.id);
      console.log('Form Data:', formData);
      
      // Create estimate via API (satellite + building data queries take ~15s)
      const response = await axios.post(`${BACKEND_URL}/api/estimates/`, {
        address: fullAddress,
        user_id: user.id,
        roof_type: formData.desired_roof_type || 'asphalt',
        current_roof_type: formData.current_roof_type || 'asphalt',
        customer_first_name: formData.customerFirstName,
        customer_last_name: formData.customerLastName,
        customer_email: formData.customerEmail || undefined,
        customer_phone: formData.customerPhone || undefined,
        scheduled_install_date: formData.scheduledInstallDate || undefined
      }, { timeout: 60000 });

      console.log('Response received:', response);
      console.log('Response data:', response.data);
      console.log('Estimate ID:', response.data.id);

      const estimateId = response.data.id;

      // If created from a CRM lead card, link the estimate back to the lead
      const leadId = formData._leadId;
      if (leadId && estimateId) {
        try {
          const token = localStorage.getItem('rooftrack_token');
          await axios.put(
            `${BACKEND_URL}/api/crm/leads/${leadId}`,
            { estimate_id: estimateId },
            { headers: { Authorization: `Bearer ${token}` } }
          );
        } catch (linkErr) {
          console.warn('Failed to link estimate to lead:', linkErr);
        }
        
        // Also check if there's an existing lead with the same address to auto-connect
        try {
          const token = localStorage.getItem('rooftrack_token');
          await axios.post(
            `${BACKEND_URL}/api/crm/leads/match-address`,
            { address: fullAddress, estimate_id: estimateId },
            { headers: { Authorization: `Bearer ${token}` } }
          );
        } catch {
          // Best effort
        }
      }

      // Redirect to the estimate detail page
      navigate(`/dashboard/estimates/${estimateId}`);
    } catch (error) {
      console.error('Estimate creation error:', error);
      if (error.code === 'ECONNABORTED') {
        setError('Request timed out. The satellite analysis is taking longer than expected. Please try again.');
      } else {
        setError(error.response?.data?.detail || 'Failed to create estimate. Please try again.');
      }
    } finally {
      setIsGenerating(false);
    }
  };

  // Handle address selection from autocomplete
  const handleAddressSelect = (addressData) => {
    setFormData(prev => ({
      ...prev,
      street_address: addressData.street_address || prev.street_address,
      city: addressData.city || prev.city,
      state: addressData.state || prev.state,
      zip_code: addressData.zip_code || prev.zip_code
    }));
  };

  const isFormValid = formData.street_address && formData.city && formData.state && formData.zip_code && formData.customerLastName;

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <Link to="/dashboard/estimates">
          <Button variant="outline" size="sm" className="mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Estimates
          </Button>
        </Link>
        <h1 className="text-3xl font-bold text-gray-900">Create New Estimate</h1>
        <p className="text-gray-600 mt-2">Generate a satellite roof measurement and estimate</p>
      </div>

      {/* Form */}
      <div className="bg-white rounded-lg shadow-lg p-8">
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800 text-sm">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Property Address */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <MapPin className="w-5 h-5 mr-2 text-blue-600" />
              Property Address
            </h2>
            <div className="space-y-4">
              <div>
                <Label htmlFor="street_address">Street Address *</Label>
                <AddressAutocomplete
                  value={formData.street_address}
                  onChange={(value) => handleChange('street_address', value)}
                  onSelectAddress={handleAddressSelect}
                  placeholder="Start typing an address..."
                />
                <p className="text-xs text-gray-500 mt-1">
                  Type to search addresses across the US
                </p>
              </div>
              
              <div>
                <Label htmlFor="address_line_2">Address Line 2 (Optional)</Label>
                <Input
                  id="address_line_2"
                  type="text"
                  value={formData.address_line_2}
                  onChange={(e) => handleChange('address_line_2', e.target.value)}
                  placeholder="Apartment, suite, unit, etc."
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-1">
                  <Label htmlFor="city">City *</Label>
                  <Input
                    id="city"
                    type="text"
                    value={formData.city}
                    onChange={(e) => handleChange('city', e.target.value)}
                    placeholder="Austin"
                    required
                  />
                </div>
                
                <div>
                  <Label htmlFor="state">State *</Label>
                  <Input
                    id="state"
                    type="text"
                    value={formData.state}
                    onChange={(e) => handleChange('state', e.target.value)}
                    placeholder="TX"
                    required
                    maxLength="2"
                  />
                </div>
                
                <div>
                  <Label htmlFor="zip_code">ZIP Code *</Label>
                  <Input
                    id="zip_code"
                    type="text"
                    value={formData.zip_code}
                    onChange={(e) => handleChange('zip_code', e.target.value)}
                    placeholder="78701"
                    required
                    maxLength="10"
                  />
                </div>
              </div>
              
              <p className="text-xs text-gray-600 mt-1">
                Separate address fields ensure accurate satellite measurement
              </p>
            </div>
          </div>

          {/* Customer Information */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <User className="w-5 h-5 mr-2 text-gray-600" />
              Customer Information
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="customerFirstName">First Name</Label>
                <Input
                  id="customerFirstName"
                  type="text"
                  value={formData.customerFirstName}
                  onChange={(e) => handleChange('customerFirstName', e.target.value)}
                  placeholder="John"
                />
              </div>

              <div>
                <Label htmlFor="customerLastName">Last Name *</Label>
                <Input
                  id="customerLastName"
                  type="text"
                  value={formData.customerLastName}
                  onChange={(e) => handleChange('customerLastName', e.target.value)}
                  placeholder="Smith"
                  required
                />
              </div>

              <div>
                <Label htmlFor="customerEmail">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="customerEmail"
                    type="email"
                    value={formData.customerEmail}
                    onChange={(e) => handleChange('customerEmail', e.target.value)}
                    placeholder="john.smith@email.com"
                    className="pl-10"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="customerPhone">Phone</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="customerPhone"
                    type="tel"
                    value={formData.customerPhone}
                    onChange={(e) => handleChange('customerPhone', e.target.value)}
                    placeholder="(555) 123-4567"
                    className="pl-10"
                  />
                </div>
              </div>
            </div>
            
            {/* SMS Consent - show only if phone is provided */}
            {formData.customerPhone && (
              <div className="mt-4">
                <SMSConsentCheckbox
                  checked={formData.smsConsent}
                  onChange={(checked) => handleChange('smsConsent', checked)}
                  companyName={user?.company || "Triple R Exteriors"}
                  required={false}
                  variant="compact"
                />
              </div>
            )}
          </div>

          {/* Optional Details */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Building className="w-5 h-5 mr-2 text-gray-600" />
              Additional Details (Optional)
            </h2>
            
            <div>
              <Label htmlFor="scheduledInstallDate">Scheduled Install Date</Label>
              <Input
                id="scheduledInstallDate"
                type="date"
                value={formData.scheduledInstallDate}
                onChange={(e) => handleChange('scheduledInstallDate', e.target.value)}
              />
            </div>
          </div>

          {/* Roof Type Selection */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Home className="w-5 h-5 mr-2 text-green-600" />
              Roof Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="current_roof_type">Current Roof Type *</Label>
                <select
                  id="current_roof_type"
                  value={formData.current_roof_type}
                  onChange={(e) => handleChange('current_roof_type', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="landmark">Landmark (Baseline Architectural)</option>
                  <option value="landmark_pro">Landmark PRO</option>
                  <option value="standing_seam">Standing Seam Metal</option>
                  <option value="luxury">Luxury Line</option>
                  <option value="other">Other</option>
                </select>
                <p className="text-xs text-gray-600 mt-1">What type of roof is currently installed?</p>
              </div>
              
              <div>
                <Label htmlFor="desired_roof_type">Desired New Roof Type *</Label>
                <select
                  id="desired_roof_type"
                  value={formData.desired_roof_type}
                  onChange={(e) => handleChange('desired_roof_type', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="landmark">Landmark (Baseline Architectural)</option>
                  <option value="landmark_pro">Landmark PRO</option>
                  <option value="standing_seam">Standing Seam Metal</option>
                  <option value="luxury">Luxury Line</option>
                </select>
                <p className="text-xs text-gray-600 mt-1">What type of roof do they want installed?</p>
              </div>
            </div>
            
            <div className="mt-4">
              <Label htmlFor="complexity">Job Complexity Multiplier *</Label>
              <select
                id="complexity"
                value={formData.complexity}
                onChange={(e) => handleChange('complexity', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="simple">Simple (×1.0) - Easy access, standard pitch, no obstacles</option>
                <option value="standard">Standard (×1.2) - Normal complexity, some obstacles</option>
                <option value="complex">Complex (×1.5) - Multiple levels, steep pitch, many obstacles</option>
                <option value="very_complex">Very Complex (×2.0) - Extremely difficult access, very steep, intricate design</option>
              </select>
              <p className="text-xs text-gray-600 mt-1">
                Affects labor costs based on job difficulty (pitch, access, obstacles, etc.)
              </p>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/dashboard/estimates')}
              className="flex-1"
              disabled={isGenerating}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!isFormValid || isGenerating}
              className="flex-1 bg-blue-600 hover:bg-blue-700"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Generating Estimate...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Create Estimate
                </>
              )}
            </Button>
          </div>
        </form>

        {/* Info Box */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="font-semibold text-blue-900 text-sm mb-2">📡 How It Works</h3>
          <ul className="text-xs text-blue-800 space-y-1">
            <li>• We'll use satellite imagery to measure the roof automatically</li>
            <li>• Estimate includes square footage, materials, and labor costs</li>
            <li>• Results are typically 99%+ accurate</li>
            <li>• You can edit details and generate proposals after creation</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default NewEstimate;
